import { EventObject } from "../../events/EventObject.js";
import { generateUniqueId, sanitizeHTML } from "../../utils/utils.js";
import { resolveComplexTemplate } from "../../utils/template/resolveComplexTemplate.js";
import { enhanceHTML } from "../../utils/template/enhanceHTML.js";
import * as templateFunctions from "../popup/templateFunctions/index.js"
import { LayerProxy } from "./proxy/LayerProxy.js";
import metadataTemplate from "./utils/LayerMetadataTemplate.md?raw";

export { Layer }

/**
 * Abstract Layer class
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.map.layer
 */
class Layer extends EventObject {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        super({
            "update": [],
            "remove": [],
            "loading": [],
            "load": [],
            "message": [],
            "enable": [],
            "disable": []
        });

        this.uniqueId = id || generateUniqueId();

        // layer metadata containers
        this.config = Object.assign({}, config || {});
        this.cache = Object.assign({}, cache || {});
        this.defaults = {};

        // init default values and define getters and setters
        this.addDefaults_({
            type: "",
            title: "",
            originalTitle: "",
            abstract: "",
            attribution: "",
            comment: "",
            citation: "",
            legendUrl: "",
            dateTimeStart: "",
            dateTimeEnd: "",
            opacity: 1,
            availableCrs: [],
            keywords: [],
            dimensions: {},
            attributeFields: {},
            icon: "fas fa-file",
            timeColumn: null,
            timeResolution: null,
            catalogId: null,
            // popup / sidebar
            metadataTemplate: null,
            popupCounter: null,
            // state
            expanded: false,
            active: false,
            filterDisabled: false,
            // settings
            styleSettings: false,
            opacitySettings: false,
            colorScaleSettings: false
        });

        this.setAll_("type", "layer");

        // apply the catalogId to the config, if it is only defined in the cache
        if (!("catalogId" in this.config) && ("catalogId" in this.cache)) {
            this.config.catalogId = this.cache.catalogId;
        }

        // internal properties
        this.messages_ = [];
        this.popupEvents_ = [];

        // active map layer state
        this.crs_ = (this.availableCrs.length > 0) ? this.availableCrs[0] : "";
        this.mapLayers_ = {};
        this.activeLayer_ = new LayerProxy();

        // framework specific map implementations
        this.layerProxies_ = {};
    }

    setAll_(property, type) {
        this.config[property] = type;
        this.cache[property] = type;
        this.defaults[property] = type;
    }

    /**
     * Helper method to get a property from the config, cache or default container
     * 
     * @param {string} name 
     * @private
     * @returns {any} property
     */
    getProperty_(name) {
        if (name in this.config) return this.config[name];
        if (name in this.cache) return this.cache[name];
        if (name in this.defaults) return this.defaults[name];
        return undefined;
    }

    /**
     * Register the getter and setter for a property and add default values for properties
     * 
     * @private
     * @param {object} defaults 
     */
    addDefaults_(defaults) {
        for (let name in defaults) {
            if (name in this) delete this[name];
            this.defaults[name] = defaults[name];
            Object.defineProperty(this, name, {
                get() { return this.getProperty_(name); },
                set(val) { this.config[name] = val; },
                configurable: true
            });
        }
    }

    /**
     * Register the getter and setter for a property and add default values for properties
     * in the exported config object. Existing values are not overwritten
     * 
     * @private
     * @param {object} config 
     */
    addConfig_(config) {
        for (let name in config) {
            if (name in this) delete this[name];
            if (!(name in this.config)) this.config[name] = config[name];
            Object.defineProperty(this, name, {
                get() { return this.config[name]; },
                set(val) { this.config[name] = val; },
                configurable: true
            });
        }
    }

    getConfig_(container) {
        container = JSON.parse(JSON.stringify(container));

        for (let key in container) {
            if (
                (container[key] === null) ||
                (((typeof container[key] == "string") || Array.isArray(container[key])) && (container[key].length == 0)) ||
                ((typeof container[key] == "object") && (Object.keys(container[key]).length == 0))

            ) delete container[key];
        }

        // remove deprecated keys
        if ("settings" in container) delete container.settings;
        if ("filter" in container) delete container.filter;
        // remove default keys
        if (container.active === false) delete container.active;
        if (container.expanded === false) delete container.expanded;

        return container;
    }

    /**
     * create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type, options) {
        if (type in this.layerProxies_) {
            const layer = new this.layerProxies_[type](options || {});

            layer.on('loading', () => this.fire("loading"));
            layer.on('load', () => this.fire("load"));
            this.mapLayers_[type] = layer;

            return layer;
        } else {
            return null;
        }
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_() { }

    getConfig() {
        const config = this.getConfig_(this.config);
        if (("catalogId" in config) && ("type" in config)) delete config.type;
        return config;
    }

    getCache() {
        return this.getConfig_(this.cache);
    }

    /**
     * Print the Metadata from this layer as html.
     */
    printMetadata(layer) {
        const container = resolveComplexTemplate(metadataTemplate, {
            layer: layer || this,
            datasetDescription: this?.dataFrame?.description || {},
            sensorMetadata: this?.sensorMetadata || {},
        }, templateFunctions, {
            markdown: true,
            postProcess: true
        });
        enhanceHTML(container, {});
        return container;
    }

    /**
     * get the events that can be triggered by buttons in a popup
     */
    getPopupEvents() {
        return this.popupEvents_.slice();
    }

    /**
     * add an event for the buttons in the popup
     */
    addPopupEvent(title, callback) {
        if ((typeof title == "string") && (typeof callback == "function")) {
            this.popupEvents_.push({
                title: title,
                callback: callback
            });
        }
    }

    /**
     * returns the map layer proxy for the
     * specific map implementation
     * 
     * @param {string} type 
     * @returns {object} map layer for framework
     */
    getLayerProxy(type) {
        return (type in this.mapLayers_)
            ? this.mapLayers_[type]
            : this.createMapLayer_(type);
    }

    /**
     * enables the map layer for the
     * specific map implementation
     * 
     * @param {string} type 
     */
    enable(type) {
        if (type in this.mapLayers_) {
            this.active = true;
            this.activeLayer_ = this.mapLayers_[type];
            this.updateMapLayer_(type);
            return true;
        }

        return false;
    }

    /**
     * Unset the active state of the layer
     */
    disable() {
        this.active = false;
    }

    /**
     * Returns the Legend Graphic for this layer.
     * May depend on current color scale.
     * 
     * @returns html <img>-tag (null or undefined if there is no legend graphic)
     */
    getLegendGraphic() { }

    /**
     * Set Filter without applying it
     *  
     * @param {object} config generic filter config 
     */
    setFilter(config) {
        this.filter = config;
    }

    /**
     * Apply the defined filter. Uses generic
     * filter syntax and transforms it internally
     */
    applyFilter() { }

    /**
     * Set the style of the layer.
     * 
     * @param {string} styleName style name
     * @returns {boolean} returns true if successful
     */
    setStyle(styleName) { }

    /**
     * Get the active style of the layer.
     */
    getStyle() { }

    /**
     * Get the active colorscale for the layer
     */
    getColorScale() { }

    /**
     * Set the colorscale for the layer
     * 
     * @param colorScale 
     */
    setColorScale(colorScale) { }

    /**
     * Dispose the Layer
     */
    dispose() { }

    /**
     * Get the transparency value
     * 
     * @returns {number} number between 0 and 1
     */
    getOpacity() {
        return this.opacity;
    }

    /**
     * Set the transparency between 0 and 1
     * @param {number} opacity style name
     */
    setOpacity(opacity) {
        this.opacity = opacity;
        this.activeLayer_.setOpacity(opacity);
    }

    /**
     * Reload the Layer on the map
     */
    reload() {
        this.activeLayer_.reload();
    }

    /**
     * Switch the projection for the visualization
     * of the layer. CRS hast to be included in availableCRS.
     * 
     * @param {string} crs target crs code
     */
    setProjection(crs) {
        crs = crs.toUpperCase();
        if (this.availableCrs.includes(crs)) {
            this.crs_ = crs;
            this.activeLayer_.setProjection(crs);
            return true;
        } else {
            return false;
        }
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        return {
            min: { y: -90, x: -180 },
            max: { y: 90, x: 180 },
            crs: "CRS:84"
        }
    }

    /**
     * Get all active messages
     * 
     * @returns {object[]} messages
     */
    getMessages() {
        // implementation of layer-specific messages needs to be done in child classes
        return [...this.messages_];
    }
}